import { DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { Component, inject, Injector, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAutoFocusDirective,
  FormHelper,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxModalComponent,
  OnyxTextFieldComponent,
  OnyxToastService,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import { PreferencesService } from '../../../../../../common/services/preferences.service';
import { ContractorsRoute } from '../../enums/contractors-route';
import { ContractorsRouterStateKey } from '../../enums/contractors-router-state-key';
import { ContractorHelper } from '../../helpers/contractor.helper';
import type { Contractor } from '../../interfaces/contractor';
import { ContractorsService } from '../../services/contractors.service';
import { NavigationHelper } from '../../../../../../common/helpers/navigation.helper';

@Component({
  selector: 'app-add-contractor-modal',
  imports: [
    OnyxModalComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxTextFieldComponent,
    OnyxButtonComponent,
    AsyncPipe,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './add-contractor-modal.component.html',
  styleUrl: './add-contractor-modal.component.scss',
})
export class AddContractorModalComponent {
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly preferencesService = inject(PreferencesService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly contractorsService = inject(ContractorsService);
  private readonly contractorHelper = inject(ContractorHelper);

  protected readonly I18N = 'contractors.addContractorModal';

  protected readonly vatIdCountries$ =
    this.dictionariesService.getVatIdCountries();

  protected form = FormHelper.buildVatIdForm(this.fb, this.preferencesService);
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  private contractor = signal<Contractor | null>(null);

  protected async next(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const { country, vatId } = this.form.getRawValue();

    this.loading.set(true);
    this.contractorsService
      .listContractors({
        filters: ContractorHelper.createVatIdFilters(country!, vatId!),
        page: 1,
        limit: Number.MAX_SAFE_INTEGER,
      })
      .subscribe((value) => {
        if (value.items.length === 0) {
          this.router.navigateByUrl(
            ContractorsRoute.ADD_CONTRACTOR,
            NavigationHelper.withOrigin(this.router, {
              state: {
                [ContractorsRouterStateKey.COUNTRY]: country,
                [ContractorsRouterStateKey.VAT_ID]: vatId,
              },
            }),
          );
          this.close$.next();
        } else {
          this.contractor.set(value.items[0]);
          this.toastService.showError({
            message: `${this.I18N}.contractorExists`,
            buttons: [
              {
                text: 'labels.show',
                callback: () => this.showExistingContractor(),
                variant: 'primary',
                color: 'black',
                size: 24,
              },
            ],
          });
        }
      })
      .add(() => this.loading.set(false));
  }

  private showExistingContractor(): void {
    const contractor = this.contractor();
    if (contractor) this.contractorHelper.openModal(contractor);
  }
}
